import type { HasTab, ISODateTimeString, RecordableEvent, RecordableEventCategory, RecordableEventName } from "../../lib/Events";
import { ProcessInfo } from "../ProcessInfo";
import { EventUpdatedInDatabase, NewEventAddedToDatabase } from "../StateEvents";
import {
	type EventReadyToWriteToDb, type UUID, type DbRecordedEvent,
	type EventMetadataParameter,
	nowAsISODateTimeString
} from "./DatabaseTypes";
import { DbEventsTableReader } from "./DbEventsTableReader";
import { recordableEventToEventReadyToWriteToDb } from "./recordableEventToDbReadyToRecordEvent";

export class DbEventsTableWriter extends DbEventsTableReader {
	constructor(recordingUuid: UUID) {
		super(recordingUuid);
	}

	addDbEvent = async <
		EVENT extends RecordableEvent
	>(
		readyToRecordEvent: EventReadyToWriteToDb<EVENT>
	): Promise<DbRecordedEvent<EVENT>> => {
		const db = await this.db;
		const mockDbRecordedEvent: DbRecordedEvent<EVENT> = {
			...readyToRecordEvent,
			whenRecordedIso: nowAsISODateTimeString(),
			recordingUuid: this.recordingUuid,
			// db.add will add the eventIndex, so we'll pretend we have it already
			...({} as {eventIndex: number}),
		} satisfies DbRecordedEvent<EVENT>;
		const eventIndex = await db.add("events", mockDbRecordedEvent);
		// Let the rest of the app know that a new event has been added to the database
		const {category, eventName} = readyToRecordEvent;
		NewEventAddedToDatabase.send({recordingUuid: this.recordingUuid, eventIndex, category, eventName});
		return { ...readyToRecordEvent, eventIndex } as DbRecordedEvent<EVENT>;
	}

	updateEvent = async<EVENT extends RecordableEvent>(
		event: DbRecordedEvent<EVENT>
	) => {
		const db = await this.db;
		const updatedEventWithWhenUpdatedTimestamp = {...event, whenUpdatedIso: nowAsISODateTimeString()};
		await db.put("events", updatedEventWithWhenUpdatedTimestamp);
		const {category, eventName, eventIndex, recordingUuid} = updatedEventWithWhenUpdatedTimestamp;
		EventUpdatedInDatabase.send({category, eventName, eventIndex, recordingUuid});
	}

	static #forRecordingUuid = new Map<UUID, DbEventsTableWriter>();
	static override forRecordingUuid = (recordingUuid: UUID) => DbEventsTableWriter.#forRecordingUuid.get(recordingUuid) ?? (() => {
		const evidenceDbForRecording = new DbEventsTableWriter(recordingUuid);
		DbEventsTableWriter.#forRecordingUuid.set(recordingUuid, evidenceDbForRecording);
		return evidenceDbForRecording;
	})();

	addRecordableEvent = <
		EVENT extends RecordableEvent
	>(
		event: EVENT & HasTab,
		options: EventMetadataParameter = {}
	) => {
		const eventReadyToWriteToDb: EventReadyToWriteToDb<EVENT> =
		recordableEventToEventReadyToWriteToDb<EVENT>(this.recordingUuid, event, options);
		return this.addDbEvent<EVENT>(eventReadyToWriteToDb);
	}

	static addRecordableEvent = <
		EVENT extends RecordableEvent
	>(
		recordingUuid: UUID,
		recordableEvent: EventReadyToWriteToDb<EVENT>,
		options: EventMetadataParameter = {}
	) => DbEventsTableWriter.forRecordingUuid(recordingUuid)
			.addRecordableEvent<EVENT>(recordableEvent, options);
	
	static getEventsByEventCategoryEventNameAndRequestId = <	
			EVENT extends RecordableEvent,
		>(
			eventCategory: EVENT["category"],
			eventName: EVENT["eventName"],
			recordingUuid: UUID,
			requestId: string
		) => DbEventsTableWriter.forRecordingUuid(recordingUuid).getEventsByCategoryThenNameThenRequest(eventCategory, eventName)(requestId);
	
		

	static override removeRecordingUuid = (recordingUuid: UUID) => DbEventsTableWriter.#forRecordingUuid.delete(recordingUuid);

}
